<script setup>
import SaveTaskTimeAction from '@frontJs/components/actions/projectManagement/tasks/SaveTaskTimeAction.vue';
import ToggleTaskTimeIsFreeAction from '@frontJs/components/actions/projectManagement/tasks/ToggleTaskTimeIsFreeAction.vue';
import ToggleTaskTimeIsUrgentAction from '@frontJs/components/actions/projectManagement/tasks/ToggleTaskTimeIsUrgentAction.vue';
import ToggleTaskTimeValidatedAtAction from '@frontJs/components/actions/projectManagement/tasks/ToggleTaskTimeValidatedAtAction.vue';
import CheckIcon from '@iconify-vue/ph/check';
import ClockIcon from '@iconify-vue/ph/clock';
import FileTextIcon from '@iconify-vue/ph/file-text';
import BoltIcon from '@iconify-vue/ph/lightning';
import BoltSlashIcon from '@iconify-vue/ph/lightning-slash';
import PencilIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import XMarkIcon from '@iconify-vue/ph/x';
import DisplayContentAction from '@js/components/actions/DisplayContentAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import CardGrid from '@js/components/cards/CardGrid.vue';
import MetricCard from '@js/components/cards/MetricCard.vue';
import CurrencyEuroIcon from '@js/components/icons/CurrencyEuroIcon.vue';
import CurrencyEuroOffIcon from '@js/components/icons/CurrencyEuroOffIcon.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { formatDate, formatTime } from '@js/utils/formatter';
import { useRoute } from '@js/utils/route';
import { toastSaved } from '@js/utils/toast';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const {
    userSessionKey = null,
    task,
    toValidate = false,
    validated = false,
    type = null,
    status = null,
    withoutFilters = false,
} = defineProps({
    userSessionKey: {
        type: String,
        required: false,
    },
    task: {
        type: Object,
        required: false,
    },
    toValidate: {
        type: Boolean,
        required: false,
    },
    validated: {
        type: Boolean,
        required: false,
    },
    type: {
        type: String,
        required: false,
    },
    status: {
        type: [String, Array],
        required: false,
    },
    withoutFilters: {
        type: Boolean,
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    globalData,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
    updateData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'id',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'ID',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.id,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'tenant.name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Client',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.tenant.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'task.name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.task.fullName,
                    length: 160,
                }),
        }),
        columnHelper.display({
            id: 'details',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Détails',
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            props.row.original.task.description
                                ? h(
                                      DisplayContentAction,
                                      {
                                          title: 'Description',
                                          content:
                                              props.row.original.task
                                                  .description,
                                      },
                                      {
                                          trigger: () =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  square: true,
                                                  title: 'Voir la description',
                                                  type: 'primary',
                                                  icon: FileTextIcon,
                                              }),
                                      },
                                  )
                                : h('div', { class: 'size-8' }, ' '),
                            props.row.original.task.internalComment
                                ? h(
                                      DisplayContentAction,
                                      {
                                          title: 'Commentaire interne',
                                          content:
                                              props.row.original.task
                                                  .internalComment,
                                      },
                                      {
                                          trigger: () =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  square: true,
                                                  title: 'Voir le commentaire interne',
                                                  type: 'secondary',
                                                  icon: FileTextIcon,
                                              }),
                                      },
                                  )
                                : h('div', { class: 'size-8' }, ' '),
                        ],
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'userFullName',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Responsable tâche',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.user.fullName,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'status',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Statut',
                }),
            cell: (props) =>
                props.row.original.task.status === 'open' ||
                props.row.original.task.status === 'to_process'
                    ? h(TextCell, {
                          text:
                              props.row.original.task.status === 'open'
                                  ? 'Ouvert'
                                  : props.row.original.task.status ===
                                      'to_process'
                                    ? 'À traiter'
                                    : 'Non définie',
                      })
                    : h(PillCell, {
                          pillValue:
                              props.row.original.task.status === 'closed'
                                  ? 'Fermé'
                                  : props.row.original.task.status ===
                                      'processed'
                                    ? 'Traité'
                                    : 'Non définie',
                          type:
                              props.row.original.task.status === 'closed'
                                  ? 'success'
                                  : props.row.original.task.status ===
                                      'processed'
                                    ? 'success'
                                    : 'secondary',
                      }),
        }),
        columnHelper.accessor(() => null, {
            id: 'minutes',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Temps facturé',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: formatTime(
                        props.row.original.invoicedHours,
                        props.row.original.invoicedMinutes,
                    ),
                    lineThrough: props.row.original.isFree,
                    bold: props.row.original.isUrgent,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'date',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Date',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: formatDate(props.row.original.date),
                }),
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (cellProps) => {
                const row = cellProps.row;
                const cellTask = task ? task : row.original.task;

                const onSuccess = () => {
                    toastSaved();
                    updateData();
                };

                return h(BaseCell, null, () => [
                    toValidate || validated
                        ? h(
                              ToggleTaskTimeValidatedAtAction,
                              {
                                  taskTime: row.original,
                                  callback: onSuccess,
                              },
                              {
                                  button: ({ action, loading }) =>
                                      h(ActionButton, {
                                          size: 'small',
                                          type: 'secondary',
                                          square: true,
                                          callback: () => action(),
                                          title: row.original.validatedAt
                                              ? 'Invalider'
                                              : 'Valider',
                                          icon: row.original.validatedAt
                                              ? XMarkIcon
                                              : CheckIcon,
                                          loading,
                                      }),
                              },
                          )
                        : null,
                    h(
                        ToggleTaskTimeIsFreeAction,
                        {
                            taskTime: row.original,
                            callback: onSuccess,
                        },
                        {
                            button: ({ action, loading }) =>
                                h(ActionButton, {
                                    size: 'small',
                                    type: 'secondary',
                                    square: true,
                                    callback: () => action(),
                                    title: row.original.isFree
                                        ? 'Doit payer'
                                        : 'Offert',
                                    icon: row.original.isFree
                                        ? CurrencyEuroOffIcon
                                        : CurrencyEuroIcon,
                                    loading,
                                }),
                        },
                    ),
                    h(
                        ToggleTaskTimeIsUrgentAction,
                        {
                            taskTime: row.original,
                            callback: onSuccess,
                        },
                        {
                            button: ({ action, loading }) =>
                                h(ActionButton, {
                                    size: 'small',
                                    type: 'secondary',
                                    square: true,
                                    callback: () => action(),
                                    title: row.original.isUrgent
                                        ? 'Normal'
                                        : 'Urgent (x2)',
                                    icon: row.original.isUrgent
                                        ? BoltSlashIcon
                                        : BoltIcon,
                                    loading,
                                }),
                        },
                    ),
                    h(
                        SaveTaskTimeAction,
                        {
                            task: cellTask,
                            taskTime: row.original,
                            callback: onSuccess,
                        },
                        {
                            button: ({ setIsOpen }) =>
                                h(ActionButton, {
                                    size: 'small',
                                    callback: () => setIsOpen(true),
                                    square: true,
                                    title: 'Modifier',
                                    icon: PencilIcon,
                                    type: 'primary',
                                }),
                        },
                    ),
                ]);
            },
        }),
    ],
    url: route('front.projectManagement.task.postTaskTimes'),
    data: {
        taskId: task ? task.id : null,
        toValidate: toValidate,
        validated: validated,
        type: type,
        status: status,
    },
    userSessionKey,
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :without-filters="withoutFilters"
        :loading="loading"
    >
        <template #before-table>
            <CardGrid>
                <MetricCard
                    title="Temps total facturé"
                    :icon="ClockIcon"
                    :value="{
                        hours: globalData.invoicedTotalHours,
                        minutes: globalData.invoicedTotalMinutes,
                    }"
                    type="time"
                />

                <MetricCard
                    title="Temps offert"
                    :icon="CurrencyEuroOffIcon"
                    :value="{
                        hours: globalData.freeTotalHours,
                        minutes: globalData.freeTotalMinutes,
                    }"
                    type="time"
                />
            </CardGrid>
        </template>

        <template #actions>
            <SaveTaskTimeAction
                v-if="task"
                :task="task"
                :callback="() => updateData()"
            >
                <template #trigger>
                    <ActionButton
                        size="small"
                        :left-icon="PlusIcon"
                        type="primary"
                    >
                        Ajouter
                    </ActionButton>
                </template>
            </SaveTaskTimeAction>
        </template>
    </DataTableLayout>
</template>
